import { Minus, Plus } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "../../utils/format";

interface QuantitySelectorProps {
  quantity: number;
  onIncrement: () => void;
  onDecrement: () => void;
  /** Nome do produto, usado nos aria-labels. */
  label: string;
  size?: "sm" | "md";
  /** Limites opcionais: o botão correspondente fica desabilitado ao alcançá-los. */
  min?: number;
  max?: number;
  className?: string;
}

export const QuantitySelector = ({
  quantity,
  onIncrement,
  onDecrement,
  label,
  size = "md",
  min,
  max,
  className,
}: QuantitySelectorProps) => {
  const btn = cn(
    "grid place-items-center rounded-full transition-colors disabled:pointer-events-none disabled:opacity-30",
    size === "sm" ? "size-7" : "size-9",
  );
  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-line bg-night p-1",
        className,
      )}
    >
      <button
        type="button"
        onClick={onDecrement}
        disabled={min !== undefined && quantity <= min}
        aria-label={`Diminuir quantidade de ${label}`}
        className={cn(btn, "text-white hover:bg-card")}
      >
        <Minus className="size-4" aria-hidden="true" />
      </button>
      <span className="relative grid w-6 place-items-center overflow-hidden text-sm font-semibold tabular-nums">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={quantity}
            initial={{ y: -12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 12, opacity: 0 }}
            transition={{ duration: 0.18 }}
            aria-live="polite"
          >
            {quantity}
          </motion.span>
        </AnimatePresence>
      </span>
      <button
        type="button"
        onClick={onIncrement}
        disabled={max !== undefined && quantity >= max}
        aria-label={`Aumentar quantidade de ${label}`}
        className={cn(btn, "bg-primary text-night hover:bg-primary-hover")}
      >
        <Plus className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
};
